import { DateRangeFilter, PageHeader, useFilters, useFormat, useReferenceOptions } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { dayRange, setOnly, shortId } from './dates';
import { MultiChoice } from './fields';
import { LogDetail } from './LogDetail';
import { LogTable, type LogQuery } from './LogTable';

type Entry = HoComponents['schemas']['AuditLogEntryDto'];

const HO = 'ho';

/**
 * Every store's audit log and HO's own (FR-AUD-03): logins, voids, refunds, discounts, approvals... with who, where
 * and when. Records are never changed (FR-AUD-02).
 */
export function AuditLogPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const stores = useReferenceOptions('org.store');
  const actions = useQuery({
    queryKey: ['ho', 'audit-actions'],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/audit-log/actions');
      if (!data) throw error;
      return data;
    },
  });
  const filters = useFilters({ store: '', action: '', from: '', to: '' });
  const [open, setOpen] = useState<Entry>();

  const storeName = (id: string | null) =>
    id ? (stores.data?.find((s) => s.value === id)?.label ?? shortId(id)) : t('logs.headOffice');
  const actionName = (code: string) =>
    t(`logs.audit.actions.${code.replace('.', '_')}` as I18nKey, { defaultValue: code });

  const load = async (q: LogQuery) => {
    const { store, action, from, to } = filters.applied;
    const { data, error } = await hoApi.GET('/api/v1/identity/audit-log', {
      params: { query: { ...setOnly({ storeId: store, action, ...dayRange(from, to) }), ...q } },
    });
    if (!data) throw error;
    return data;
  };

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('logs.audit.title')} description={t('logs.audit.hint')} />
      <LogTable<Entry>
        label={t('logs.audit.title')}
        queryKey={['ho', 'audit-log']}
        load={load}
        rowId={(e) => e.id}
        filters={filters}
        filterFields={
          <>
            <MultiChoice
              id="audit-store"
              label={t('logs.columns.store')}
              choices={[
                { id: HO, label: t('logs.headOffice') },
                ...(stores.data ?? []).map((s) => ({ id: s.value, label: s.label })),
              ]}
              value={filters.draft.store}
              onChange={(v) => filters.set('store', v)}
            />
            <MultiChoice
              id="audit-action"
              label={t('logs.columns.action')}
              choices={(actions.data ?? []).map((code) => ({ id: code, label: actionName(code) }))}
              value={filters.draft.action}
              onChange={(v) => filters.set('action', v)}
            />
            <DateRangeFilter
              id="audit-date"
              legendText={t('logs.columns.time')}
              from={filters.draft.from}
              to={filters.draft.to}
              onChange={(r) => filters.setDraft((d) => ({ ...d, ...r }))}
            />
          </>
        }
        onOpen={setOpen}
        columns={[
          { key: 'time', header: t('logs.columns.time'), render: (e) => format.dateTime(e.occurredAt) },
          { key: 'store', header: t('logs.columns.store'), render: (e) => storeName(e.storeId) },
          {
            key: 'staff',
            header: t('logs.columns.staff'),
            render: (e) => (e.staffCode ? `${e.staffCode} · ${e.userName ?? ''}` : '—'),
          },
          { key: 'action', header: t('logs.columns.action'), render: (e) => actionName(e.actionCode) },
          {
            key: 'target',
            header: t('logs.columns.target'),
            render: (e) =>
              e.targetType ? <span className="pos-log__id">{`${e.targetType} ${shortId(e.targetId)}`}</span> : '—',
          },
        ]}
      />
      {open && (
        <LogDetail
          title={actionName(open.actionCode)}
          label={t('logs.audit.title')}
          payload={open.details}
          onClose={() => setOpen(undefined)}
          fields={[
            [t('logs.columns.time'), format.dateTime(open.occurredAt)],
            [t('logs.columns.store'), storeName(open.storeId)],
            [t('logs.columns.staff'), open.staffCode ? `${open.staffCode} · ${open.userName ?? ''}` : '—'],
            [t('logs.columns.action'), open.actionCode],
            [t('logs.columns.target'), open.targetType ? `${open.targetType} ${open.targetId ?? ''}` : '—'],
            [t('logs.columns.terminal'), open.terminalId],
            [t('logs.columns.approval'), open.approvalId],
          ]}
        />
      )}
    </section>
  );
}
